import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EditDelete from "../../../components/EditDelete";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp, formatDate } from "../../../utils/common";
import { useTranslation } from "react-i18next";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import usePaidInvoice from "../../../hooks/usePaidInvoice";
import AdvanceFilter from "./components/AdvanceFilter";
import useAuth from "../../../hooks/useAuth";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function GstInvoice(props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { width: mainContentWidth } = useMainContentDimensions();

  const { state } = useLocation();
  const { t } = useTranslation();

  const { radUserId } = props;

  const today = formatDate(new Date());

  const currentPayload = {
    filters: [
      {
        fieldName: "invoiceDate",
        fieldValue: `${today} 00:00:00,${today} 23:59:59`,
        op: "btn",
        dataType: "date",
        condition: "and",
        isJoin: false,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "invoiceDate",
        sort: "desc",
      },
    ],
  };

  useEffect(() => {
    if (radUserId != undefined) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
    } else {
      setPayload(currentPayload);
    }
  }, [radUserId]);

  useEffect(() => {
    if (user != undefined && user.businessType == "USER") {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: user.radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
    } else {
      setPayload(currentPayload);
    }
  }, [user]);

  const [sortModel, setSortModel] = useState([
    {
      field: "renewDate",
      sort: "desc",
    },
  ]);
  const [filterFormData, setFilterFormData] = useState({
    invoiceDate: [new Date(), new Date()],
    invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
  });
  const { height } = useWindowDimensions();
  const {
    getAllGstInvoice,
    paidInvoices,
    paging,
    downloadGstInvoiceFile,
    isLoadingDownload,
    downloadFileType,
  } = usePaidInvoice();
  const { openDialog } = useDialog();

  const [payload, setPayload] = useState({});

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    if (Object.keys(payload).length != 0) {
      await getAllGstInvoice(payload);
    }
  };

  const exportFile = async (fileType) => {
    await downloadGstInvoiceFile(fileType, payload);
  };

  const refreshPage = () => {
    if (radUserId != undefined) {
      const payloadValues = {
        ...currentPayload,
        filters: [
          ...currentPayload.filters,
          {
            fieldName: "radUserId",
            fieldValue: radUserId,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
            attributeName: "radUser",
            entityName: "RadUserEntity",
          },
        ],
      };
      setPayload(payloadValues);
      setFilterFormData({
        invoiceDate: [new Date(), new Date()],
        invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
      });
      filterResetFunc();
      setPaginationModel({
        page: 0,
        pageSize: 100,
      });
    } else {
      setPayload(currentPayload);
      setFilterFormData({
        invoiceDate: [new Date(), new Date()],
        invoiceDateStr: `${today} 00:00:00,${today} 23:59:59`,
      });
    }
    filterResetFunc();
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Invoices</Typography>
        <Typography>Gst</Typography>
      </Breadcrumbs>
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    setSortModel(newModel);
    if (newModel.length !== 0) {
      const backendModel = newModel.map((item) => {
        let field = item.field;
        if (field === "renewDate") field = "invoiceDate";
        else if (field === "userName") field = "radUser.userName";
        else if (field === "mobileNo") field = "radUser.mobileNo";
        else if (field === "finalInvoiceAmount") field = "finalInvoiceAmt";
        return { ...item, field };
      });
      setPayload((prev) => ({ ...prev, sortFields: backendModel }));
    } else {
      setPayload((prev) => ({ ...prev, sortFields: [] }));
    }
  };

  const viewInvoice = (id) => {
    navigate("/user/invoice", { state: { id: id } });
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      filter: false,
      sortable: false,
      width: 100,
      renderCell: (params) => {
        const onClick = (e) => {
          e.stopPropagation();
        };
        return (
          <EditDelete
            rowClick={onClick}
            clickIcon={() => viewInvoice(params.id)}
            menus={["invoice_view"]}
            rowId={params.id}
          />
        );
      },
    },
    {
      field: "invoiceNo",
      headerName: "Invoice No",
      width: 130,
    },
    {
      field: "userName",
      headerName: "User Name",
      width: 300,
    },
    {
      field: "mobileNo",
      headerName: "Mobile No",
      width: 130,
    },
    {
      field: "address",
      headerName: "Address",
      width: 300,
      sortable: false, // Disabling sorting on composite field
    },
    {
      field: "renewDate",
      headerName: "Invoice Date",
      width: 200,
    },
    {
      field: "finalInvoiceAmount",
      headerName: "Invoice Amount",
      width: 150,
    },
    {
      field: "gstNo",
      headerName: "Gst Number",
      width: 130,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            downloadFile={exportFile}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {t("gst_invoices")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            columns={columns}
            rows={paidInvoices}
            refreshPage={fetchData}
            getRowId={(row) => row["renewId"]}
            rowCount={paging.totalNumberOfRecords}
            loading={false}
            rowsPerPageOptions={[5]}
            pagination
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMode="server"
            onPaginationModelChange={onPageChange}
            onPageSizeChange={(pageSize) => onPageChange(pageSize)}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default GstInvoice;
